import { ReactElement } from 'react';
import { TextKey } from '@mews-commander/enums';
import { lt, showErrorAlert, showSuccessAlert, useDialogAction } from '@mews/framework';
import { Button, Dialog } from '@optimus-web/core';
import { EmailWhitelistConfiguration } from '@mews-commander/data-access';
import { useDeleteWhitelistedUserEmail } from '../actions';

interface DeleteWhitelistedUserEmailProps {
    row: EmailWhitelistConfiguration;
    renderButtonComponent: (onClick: () => void) => ReactElement;
}

export const DeleteWhitelistedUserEmail = ({ row, renderButtonComponent }: DeleteWhitelistedUserEmailProps) => {
    const { id, email, name } = row;
    const { mutateAsync } = useDeleteWhitelistedUserEmail({
        onSuccess: () => {
            showSuccessAlert({ description: lt(TextKey.WhitelistedUserEmailsDeleteAlertSuccess, { email, name }) });
        },
        onError: () => {
            showErrorAlert({ description: lt(TextKey.WhitelistedUserEmailsDeleteAlertError, { email, name }) });
        },
    });

    const deleteAction = useDialogAction(async () => mutateAsync({ ids: [id] }));

    return (
        <>
            {renderButtonComponent(deleteAction.handlers.handleOpen)}
            {deleteAction.states.isDialogOpened ? (
                <Dialog
                    title={lt(TextKey.WhitelistedUserEmailsDeleteEmailDialogTitle, { email })}
                    footer={{
                        primary: (
                            <Button
                                size="medium"
                                variant="destructive"
                                onClick={deleteAction.handlers.handleConfirm}
                                type="button"
                            >
                                {lt(TextKey.Delete)}
                            </Button>
                        ),
                        secondary: (
                            <Button
                                size="medium"
                                variant="tertiary"
                                onClick={deleteAction.handlers.handleCancel}
                                type="button"
                            >
                                {lt(TextKey.GoBack)}
                            </Button>
                        ),
                    }}
                    active
                    loading={deleteAction.states.isProcessing}
                    onClose={deleteAction.handlers.handleCancel}
                >
                    {lt(TextKey.WhitelistedUserEmailsDeleteEmailDialogBody, { email, name })}
                </Dialog>
            ) : null}
        </>
    );
};
